import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Link,
  Progress,
  Spinner,
  Stack,
  Text,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { FiChevronRight, FiChevronLeft, FiSave, FiCheck } from 'react-icons/fi';
import apiClient from '../services/apiClient';
import * as nominationService from '../services/api/nominationService';
import * as settingsService from '../services/api/settingsService';
import API from '../config/api';
import { trackEvent } from '../services/analytics/analyticsService';
// SMS service is now handled by the backend
import { loadDraft, saveDraft, deleteDraft } from '../services/draftService';
import DraftNotification from '../components/DraftNotification';
import DraftLoadingIndicator from '../components/DraftLoadingIndicator';

// Form step components
import IntroductionForm from '../components/forms/IntroductionForm';
import PersonalInfoForm from '../components/forms/PersonalInfoForm';
import DocumentsForm from '../components/forms/DocumentsForm';
import NominationForm from '../components/forms/NominationForm';
import ReviewForm from '../components/forms/ReviewForm';

// Context
import { NominationFormProvider, useNominationForm } from '../context/NominationFormContext';

/**
 * ApplicationFormContent component
 * Contains the actual form implementation
 */
const ApplicationFormContent = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { id } = useParams();
  const isEditMode = !!id; // A clearer way to check for edit mode

  // Get form state from context
  const { state, dispatch } = useNominationForm();
  const { currentStep, formData, formProgress, nominationId } = state;

  // Local state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode); // Start loading if in edit mode
  const [showDraftNotification, setShowDraftNotification] = useState(false);
  const [, setDraftInfo] = useState(null);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [hideFormDueToDraft, setHideFormDueToDraft] = useState(false);
  const [, setHasSavedDraft] = useState(false);
  const [isNominationOpen, setIsNominationOpen] = useState(true);
  const [isCheckingNominationStatus, setIsCheckingNominationStatus] = useState(true);

  const totalSteps = 5;
  const stepTitles = [
    'المقدمة والتعليمات',
    'المعلومات الشخصية',
    'المستندات',
    'الترشيح',
    'المراجعة',
  ];

  const [stepsAttempted, setStepsAttempted] = useState({
    step0: true,
  });

  // Check nomination status first
  useEffect(() => {
    const checkNominationStatus = async () => {
      try {
        setIsCheckingNominationStatus(true);
        const response = await settingsService.getNominationStatus();
        setIsNominationOpen(response.is_open);
      } catch (error) {
        console.error('Error checking nomination status:', error);
        // Default to closed if there's an error
        setIsNominationOpen(false);
      } finally {
        setIsCheckingNominationStatus(false);
      }
    };

    checkNominationStatus();
  }, []);

  useEffect(() => {
    const loadApplicationData = async () => {
        // التحقق من وجود مسودة محفوظة أولاً (إذا لم نكن في وضع التعديل)
        if (!isEditMode) {
            const savedDraft = await loadDraft();
            if (savedDraft) {
                setHasSavedDraft(true);
                setShowDraftNotification(true);
                setHideFormDueToDraft(true); // إخفاء الفورم حتى يتخذ المستخدم قراراً
                setDraftInfo(savedDraft);
                setIsLoading(false);
                return;
            }
        }
        
        // نتحقق من وجود id للتأكد من أننا في وضع التعديل الفعلي
        if (isEditMode && id) {
            setIsLoading(true);
            try {
                const response = await apiClient.applications.getById(id);
                
                // *** التعديل هنا: اقرأ الـ response مباشرةً ***
                const applicationData = response;

                if (applicationData && applicationData.id) {

                    // تنسيق البيانات للنموذج
                    const formattedData = {
                        personalInfo: {
                            firstName: applicationData.firstName || '',
                            fatherName: applicationData.father_name || '',
                            lastName: applicationData.lastName || '',
                            email: applicationData.email || '',
                            phone: applicationData.phone || '',
                            nationalId: applicationData.nationalId || '',
                            birthDate: applicationData.birthDate || '',
                            city: applicationData.city || '',
                            district: applicationData.district || '',
                            street: applicationData.street || '',
                            buildingNumber: applicationData.buildingNumber || '',
                            postalCode: applicationData.postalCode || '',
                            // reflect saved membership status in the form
                            is_member: applicationData.is_member ?? null,
                        },
                        documents: {
                            // هذه تعتمد على كيفية التعامل مع الملفات، لكن الروابط يجب أن تتطابق
                            personalPhoto: applicationData.personal_photo_url || null,
                            securityClearance: applicationData.security_clearance_document_url || null,
                            cv: applicationData.cv_document_url || null,
                            additionalDocuments: applicationData.additionalDocuments || [],
                        },
                        nomination: {
                            branch: applicationData.branch || '',
                            center: applicationData.center_id || '',

                            associationKnowledge: applicationData.society_knowledge || '',
                            socialResponsibility: applicationData.social_responsibility_contribution || '',
                            vision2030Programs: applicationData.vision_2030_contribution || '',
                            centerIncomeIdeas: applicationData.center_income_ideas || '',
                            readRegulations: applicationData.regulations_acknowledgement || false,
                            confirmResponsibilities: applicationData.commitment_acknowledgement || false,
                            acceptTerms: applicationData.terms_and_conditions_acknowledgement || false,
                        }
                    };

                    dispatch({ type: 'UPDATE_FORM_DATA', payload: formattedData });
                    dispatch({
                        type: 'UPDATE_FORM_PROGRESS',
                        payload: { step1: true, step2: true, step3: true, step4: true },
                    });
                    // الانتقال للخطوة التي كان عليها المستخدم أو الخطوة الأولى بعد المقدمة
                    dispatch({ type: 'SET_CURRENT_STEP', payload: applicationData.current_step || 1 });

                    toast({
                        title: "تم تحميل بيانات الطلب",
                        status: "success",
                        duration: 3000,
                        isClosable: true,
                    });
                } else {
                    throw new Error("لم يتم العثور على بيانات الطلب.");
                }
            } catch (error) {
                console.error("Error loading application data:", error);
                toast({
                    title: "خطأ في تحميل البيانات",
                    description: "حدث خطأ أثناء تحميل بيانات الطلب.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
                navigate('/'); // إعادة التوجيه للصفحة الرئيسية عند الفشل
            } finally {
                setIsLoading(false);
            }
        } else {
            // إذا لم يكن هناك id، لا تقم بالتحميل
            setIsLoading(false);
        }
    };

    loadApplicationData();
    // أضفنا dispatch, navigate, toast إلى مصفوفة الاعتماديات
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [id, isEditMode]);


  // دوال إدارة المسودة
  const handleLoadDraft = useCallback(async () => {
    setIsLoadingDraft(true);
    // Track draft load attempt
    trackEvent('Draft Operation', 'Load Attempt', 'Application Form');
    try {
      const savedDraft = await loadDraft();
      if (savedDraft) {
        // تحديث بيانات النموذج من المسودة
        dispatch({ type: 'UPDATE_FORM_DATA', payload: savedDraft.formData });
        
        // إعادة تعيين حالة التقدم للمستندات (الخطوة 2) لأنه لا يتم حفظ الملفات
        const updatedProgress = {
          ...savedDraft.formProgress,
          step2: false // إجبار المستخدم على إعادة اختيار الملفات
        };
        dispatch({ type: 'UPDATE_FORM_PROGRESS', payload: updatedProgress });
        
        // إذا كان المستخدم في خطوة بعد المستندات، أعده إلى خطوة المستندات
        const targetStep = savedDraft.currentStep > 2 ? 2 : savedDraft.currentStep;
        dispatch({ type: 'SET_CURRENT_STEP', payload: targetStep });
        if (savedDraft.nominationId) {
          dispatch({ type: 'SET_NOMINATION_ID', payload: savedDraft.nominationId });
        }
        
        // Track successful draft loading
        trackEvent('Draft Operation', 'Load Success', `Loaded draft from step ${savedDraft.currentStep}`);
        
        setShowDraftNotification(false);
        setHasSavedDraft(false);
        setHideFormDueToDraft(false); // إظهار الفورم مرة أخرى
        
        toast({
          title: 'تم تحميل المسودة',
          description: 'تم استرجاع بياناتك المحفوظة. ستحتاج لإعادة اختيار الملفات.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('خطأ في تحميل المسودة:', error);
      toast({
        title: 'خطأ في تحميل المسودة',
        description: 'حدث خطأ أثناء تحميل المسودة المحفوظة',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDraft(false);
    }
  }, [dispatch, toast]);

  // دالة إعادة تعيين النموذج بالكامل
  const resetForm = useCallback(() => {
    // إعادة تعيين حالة النموذج في السياق
    dispatch({ type: 'RESET_FORM' });
    
    // إعادة تعيين الحالة المحلية
    setShowDraftNotification(false);
    setHasSavedDraft(false);
    setHideFormDueToDraft(false);
    setDraftInfo(null);
    
    // إعادة تعيين الخطوات المحاولة
    setStepsAttempted({
      step0: true
    });
    
    // إعادة التوجيه للخطوة الأولى
    dispatch({ type: 'SET_CURRENT_STEP', payload: 0 });
  }, [dispatch]);
  
  // دالة رفض المسودة - تظهر النموذج فقط بدون إعادة تعيين البيانات
  const handleDismissDraft = useCallback(() => {
    setShowDraftNotification(false);
    setHasSavedDraft(false);
    setHideFormDueToDraft(false); // إظهار الفورم عند رفض المسودة
  }, []);
  
  // دالة حذف المسودة - تحذف البيانات وتعيد تعيين النموذج
  const handleDeleteDraft = useCallback(() => {
    try {
      // حذف المسودة من التخزين المحلي
      const isDeleted = deleteDraft();
      
      if (isDeleted) {
        // إعادة تعيين النموذج بالكامل
        resetForm();
        
        toast({
          title: 'تم حذف المسودة',
          description: 'تم حذف المسودة المحفوظة وإعادة تعيين النموذج بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('فشل في حذف المسودة من التخزين المحلي');
      }
    } catch (error) {
      console.error('خطأ في حذف المسودة:', error);
      toast({
        title: 'خطأ في حذف المسودة',
        description: error.message || 'حدث خطأ أثناء محاولة حذف المسودة',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [resetForm, toast]);

// استخدم useCallback لتثبيت مرجع الدالة ومنع إعادة إنشائها
const handleFormDataChange = useCallback((step, data, isValid) => {
  // Update the form data
  dispatch({
      type: 'UPDATE_STEP_DATA',
      payload: {
          step: step,
          data: data,
          isValid: isValid
      }
  });
  
  // If the step is valid, update the form progress state directly
  if (isValid) {
    dispatch({
      type: 'UPDATE_FORM_PROGRESS',
      payload: { [`step${step}`]: true }
    });
  }
}, [dispatch]); // dispatch مضمونة بأن تكون ثابتة

  const handleStartApplication = () => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: 1 });
  };

  const handleNext = async (skipValidation = false) => {
    setStepsAttempted(prev => ({ ...prev, [`step${currentStep}`]: true }));

    // Skip validation if environment variable is set to true
    const shouldBypassValidation = process.env.REACT_APP_DISABLE_FORM_VALIDATION === 'true';

    // Special case for step 3 (Nomination form) - force update form progress if all fields are filled
    if (currentStep === 3 && !skipValidation && !shouldBypassValidation) {
      const nomination = formData.nomination || {};
      const hasAllRequiredFields = nomination.branch && 
                                  nomination.center && 
                                  nomination.readRegulations && 
                                  nomination.confirmResponsibilities && 
                                  nomination.acceptTerms;
      
      const hasAllTextFields = nomination.associationKnowledge && 
                              nomination.socialResponsibility && 
                              nomination.vision2030Programs && 
                              nomination.centerIncomeIdeas;
      
      // If all fields are present (including URLs), mark step 3 as valid
      if (hasAllRequiredFields && hasAllTextFields) {
        dispatch({
          type: 'UPDATE_FORM_PROGRESS',
          payload: { step3: true }
        });
      }
    }

    if (shouldBypassValidation === true || skipValidation === true) {
      if (currentStep < totalSteps - 1) {
        dispatch({ type: 'SET_CURRENT_STEP', payload: currentStep + 1 });
        window.scrollTo(0, 0);
      }
      return;
    }

    // Force validation check on the current step
    let isValid = false;
    
    // Check which step we're on and trigger appropriate validation
    switch (currentStep) {
      case 1: // Personal Info step
        // Get the current validation state from the form context
        isValid = formProgress.step1;
        
        // Additional checks for step 1
        const personalInfo = formData.personalInfo || {};
        
        // First check if all required fields are filled
        const requiredFields = ['firstName', 'lastName', 'fatherName', 'birthDate', 'phone', 'email', 'nationalId', 'city', 'district'];
        const missingFields = [];
        
        requiredFields.forEach(field => {
          if (!personalInfo[field]) {
            missingFields.push(field);
          }
        });
        
        if (missingFields.length > 0) {
          toast({
            title: "بيانات غير مكتملة",
            description: "يرجى إكمال جميع الحقول المطلوبة بشكل صحيح للمتابعة.",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        
        // Then check for existing nomination
        if (personalInfo.has_existing_nomination === true) {
          toast({
            title: "تحت التقييم مسبقًا",
            description: "لقد قمت بتقديم طلب ترشيح مسبقًا. لا يمكن تقديم أكثر من طلب.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        
        // Finally check if ID is verified
        if (personalInfo.is_verified !== true) {
          toast({
            title: "التحقق مطلوب",
            description: "يرجى التحقق من رقم الهوية الوطنية قبل المتابعة.",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        break;
        
      case 2: // Documents step
        isValid = formProgress.step2;
        break;
        
      case 3: // Nomination step
        // Check all required fields in nomination form
        const nomination = formData.nomination || {};
        const nominationMissingFields = [];
        
        // Check for branch and center
        if (!nomination.branch) nominationMissingFields.push('الفرع');
        if (!nomination.center) nominationMissingFields.push('المركز');
        
        // Check for checkboxes and radio buttons
        if (!nomination.readRegulations) nominationMissingFields.push('الاطلاع على اللوائح');
        if (!nomination.confirmResponsibilities) nominationMissingFields.push('تأكيد المسؤوليات');
        if (!nomination.acceptTerms) nominationMissingFields.push('الشروط والأحكام');
        
        // Check for text fields
        const textFields = [
          { field: 'associationKnowledge', label: 'المعرفة بالجمعية' },
          { field: 'socialResponsibility', label: 'المسؤولية الاجتماعية' },
          { field: 'vision2030Programs', label: 'دورك في تحقيق رؤية 2030' },
          { field: 'centerIncomeIdeas', label: 'تنمية الموارد المالية' }
        ];
        
        textFields.forEach(({ field, label }) => {
          const value = nomination[field];
          if (!value || value.trim().length < 50) {
            nominationMissingFields.push(label);
          }
        });
        
        // If there are missing fields, show detailed error message
        if (nominationMissingFields.length > 0) {
          toast({
            title: "بيانات غير مكتملة",
            description: `يرجى إكمال الحقول التالية:\n- ${nominationMissingFields.join('\n- ')}`,
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        
        // If we got here, all fields are valid
        isValid = true;
        
        // Update form progress to reflect validation
        dispatch({
          type: 'UPDATE_FORM_PROGRESS',
          payload: { step3: true }
        });
        break;
        
      default:
        isValid = true;
    }

    // If validation fails, show toast and prevent proceeding
    if (!isValid) {
      toast({
        title: "بيانات غير مكتملة",
        description: "يرجى إكمال جميع الحقول المطلوبة بشكل صحيح للمتابعة.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // If we passed all validation, proceed to next step
    if (currentStep < totalSteps - 1) {
      // Track the step navigation in Google Analytics
      trackEvent('Form Navigation', 'Next Step', `From Step ${currentStep} to Step ${currentStep + 1}`);
      
      dispatch({ type: 'SET_CURRENT_STEP', payload: currentStep + 1 });
      window.scrollTo(0, 0);
    }
  };
  const handlePrevious = () => {
    if (currentStep > 1) { // Prevents going back from Personal Info to Intro
      // Track the step navigation in Google Analytics
      trackEvent('Form Navigation', 'Previous Step', `From Step ${currentStep} to Step ${currentStep - 1}`);
      
      dispatch({ type: 'SET_CURRENT_STEP', payload: currentStep - 1 });
      window.scrollTo(0, 0);
    }
  };

  const handleSaveProgress = async () => {
    setIsSaving(true);
    // Track draft save attempt
    trackEvent('Draft Operation', 'Save Attempt', `Step ${currentStep}`);
    try {
        // حفظ جميع بيانات النموذج في التخزين المحلي
        const draftData = {
            version: '1.0',
            currentStep,
            nominationId,
            formData,
            formProgress,
            lastSaved: new Date().toISOString()
        };
        
        await saveDraft(draftData);
        
        // Track successful draft save
        trackEvent('Draft Operation', 'Save Success', `Step ${currentStep}`);
        
        toast({
            title: 'تم حفظ المسودة',
            description: 'تم حفظ جميع بياناتك بنجاح. يمكنك العودة لاحقًا لإكمال التقديم.',
            status: 'success',
            duration: 5000,
            isClosable: true,
        });
        
        setHasSavedDraft(true);
    } catch (error) {
        console.error('خطأ في حفظ المسودة:', error);
        toast({
            title: 'خطأ في حفظ المسودة',
            description: error.message || 'حدث خطأ أثناء حفظ بيانات طلبك.',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    } finally {
        setIsSaving(false);
    }
  };

  const [submittedData, setSubmittedData] = useState(null);

  const handleSubmit = async (updatedFormData) => {
    setIsSubmitting(true);
    // Track form submission attempt
    trackEvent('Form Submission', 'Attempt', 'Application Form');
    try {
        // Use the updatedFormData if provided (which includes uploaded file data), otherwise use the current formData
        const dataToSubmit = updatedFormData || formData;
        
        // إرسال البيانات مباشرة بدون الحاجة إلى معرف ترشيح
        // سيتم إنشاء ترشيح جديد في الباك إند وربط الملفات به
        const response = await nominationService.submitNomination({
            ...dataToSubmit,
            // إرسال معرفات الملفات المرفوعة لربطها بالترشيح الجديد
            fileIds: Object.values(dataToSubmit.documents || {})
                .filter(doc => doc && doc.id)
                .map(doc => doc.id)
        });
        setIsSubmitting(false);
        if (response.success) {
            // Track successful form submission
            trackEvent('Form Submission', 'Success', `Application ID: ${response.data?.application_number || 'Unknown'}`);
            
            // حفظ بيانات الاستجابة لعرضها في نافذة النجاح
            setSubmittedData(response.data);
            
            // إرسال رسالة SMS للمتقدم بعد نجاح التقديم
            try {
                // التأكد من وجود رقم هاتف
                if (dataToSubmit.personalInfo && dataToSubmit.personalInfo.phone) {
                    // استدعاء نقطة النهاية العامة لإرسال رسائل تأكيد الطلبات
                    await apiClient.publicApiClient.post(API.notifications.applicationConfirmation, {
                        phone_number: dataToSubmit.personalInfo.phone,
                        first_name: dataToSubmit.personalInfo.firstName || '',
                        last_name: dataToSubmit.personalInfo.lastName || '',
                        application_number: response.data?.application_number || 'غير متوفر'
                    });
                    console.log('تم إرسال رسالة SMS بنجاح');
                }
            } catch (smsError) {
                console.error('خطأ في إرسال رسالة SMS:', smsError);
                // لا نريد أن نوقف العملية إذا فشل إرسال الرسالة
            }
            
            onOpen(); // Open success modal
        } else {
            throw new Error(response.message || 'حدث خطأ أثناء تقديم الطلب');
        }
    } catch (error) {
        setIsSubmitting(false);
        toast({
            title: 'خطأ في تقديم الطلب',
            description: error.message || 'يرجى مراجعة بياناتك والمحاولة مرة أخرى.',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    }
  };

  const handleSuccessModalClose = () => {
    onClose();
    // حذف المسودة عند التقديم بنجاح
    try {
      deleteDraft();
    } catch (error) {
      console.error('خطأ في حذف المسودة:', error);
    }
    dispatch({ type: 'RESET_FORM' }); // Reset form state
    navigate('/');
  };

  const renderStepForm = () => {
    switch (currentStep) {
      case 0:
        return <IntroductionForm onStart={handleStartApplication} />;
      case 1:
        return (
          <PersonalInfoForm
            formData={formData.personalInfo}
            onChange={(data, isValid) => handleFormDataChange(1, data, isValid)}
            showValidation={stepsAttempted.step1}
          />
        );
      case 2:
        return (
          <DocumentsForm
            formData={formData.documents}
            onChange={(data, isValid) => handleFormDataChange(2, data, isValid)}
            showValidation={stepsAttempted.step2}
          />
        );
      case 3:
        return (
          <NominationForm
            formData={formData.nomination}
            onChange={(data, isValid) => handleFormDataChange(3, data, isValid)}
            showValidation={stepsAttempted.step3}
          />
        );
      case 4:
        return <ReviewForm formData={formData} onSubmit={handleSubmit} isSubmitting={isSubmitting} />;
      default:
        return <div>خطأ: خطوة غير معروفة.</div>;
    }
  };
  
  // Show loading while checking nomination status
  if (isCheckingNominationStatus) {
    return (
      <Container maxW="container.lg" py={{ base: 4, md: 8 }} px={{ base: 2, md: 6 }}>
        <Flex justify="center" align="center" minHeight="50vh" direction="column">
          <Spinner size="xl" color="primary.500" mb={4} />
          <Text>جاري التحقق من حالة الترشيح...</Text>
        </Flex>
      </Container>
    );
  }

  // Show nomination closed message
  if (!isNominationOpen && !isEditMode) {
    return (
      <Container maxW="container.lg" py={{ base: 4, md: 8 }} px={{ base: 2, md: 6 }}>
        <Flex justify="center" align="center" minHeight="50vh" direction="column">
          <Box textAlign="center" p={8} borderRadius="lg">
            <Heading as="h2" size="lg" mb={4}>
              انتهت فترة الترشيح
            </Heading>
            <Text fontSize="lg" mb={6}>
              نعتذر لإبلاغكم بأن فترة الترشيح قد انتهت.
              <br />
              يرجى متابعة الإعلانات للتعرف على مواعيد فترات الترشيح القادمة.
            </Text>
            <Button 
              colorScheme="blue" 
              onClick={() => navigate('/')}
              size="lg"
              mb={6}
            >
              العودة إلى الصفحة الرئيسية
            </Button>
            <Box 
              mt={6}
              pt={4}
              borderTop="1px"
              borderColor="gray.200"
              opacity={0.8}
            >
              <Flex 
                direction="column"
                align="center" 
                justify="center" 
                textAlign="center"
                fontSize="sm"
                color="gray.600"
                spacing={2}
              >
                <Text mb={2}>
                  مجالسنا هو نظام إلكتروني مطور من قبل شركة زخم لتقنية المعلومات
                </Text>
                <Flex align="center" justify="center">
                  <Text ml={2}>
                    للتواصل معنا:
                  </Text>
                  <Link href="https://zakhm.sa" isExternal ml={2}>
                    <Image 
                      src="/images/Zakhm.svg" 
                      alt="Zakhm.sa" 
                      height="20px"
                      fallbackSrc="https://majalisna-files-2025.s3.me-south-1.amazonaws.com/public-folder/zakhmlogo.svg"
                    />
                  </Link>
                </Flex>
                <Link href="https://zakhm.sa" isExternal color="blue.500" fontSize="xs">
                  https://zakhm.sa
                </Link>
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={8}>
      <>
        {isLoading ? (
          <Flex justify="center" align="center" minHeight="50vh" direction="column">
            <Spinner size="xl" color="primary.500" mb={4} />
            <Text>جاري تحميل بيانات الطلب...</Text>
          </Flex>
        ) : (
        <Stack spacing={8}>
          <Box textAlign="center" px={{ base: 2, md: 0 }}>
            <Heading as="h1" size={{ base: "lg", md: "xl" }} mb={2}>
              نموذج طلب الترشيح
            </Heading>
            <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
              يرجى تعبئة النموذج التالي بدقة لتقديم طلب الترشيح
            </Text>
          </Box>

          {/* إشعار المسودة المحفوظة */}
          {showDraftNotification && (
            <DraftNotification 
              onLoadDraft={handleLoadDraft}
              onDismiss={handleDismissDraft}
              onDelete={handleDeleteDraft}
            />
          )}

          {/* Progress bar */}
          {!hideFormDueToDraft && (
            <Box px={{ base: 2, md: 0 }}>
              <Progress
                value={((currentStep) / (totalSteps-1)) * 100} // Progress based on actual form steps
                size="sm"
                colorScheme="primary"
                borderRadius="md"
                mb={4}
                hasStripe
                isAnimated={currentStep < totalSteps -1}
              />
            
            <Flex justify="space-between" >
              {stepTitles.map((title, index) => (
                <Box
                  key={`desktop-${index}`}
                  textAlign="center"
                  fontWeight={currentStep === index ? 'bold' : 'normal'}
                  color={
                    currentStep === index
                      ? 'primary.500'
                      : currentStep > index
                      ? 'green.500'
                      : 'gray.500'
                  }
                  flex="1"
                  opacity={currentStep === 0 && index > 0 ? 0.5 : 1}
                  display={{ base: index === 0 ? 'none' : 'block', md: 'block' }} // Hide intro on mobile
                >
                  <Flex
                    w="30px"
                    h="30px"
                    borderRadius="full"
                    bg={
                      currentStep === index
                        ? 'gray.600'
                        : currentStep > index
                        ? 'green.500'
                        : 'gray.200'
                    }
                    color="white"
                    justify="center"
                    align="center"
                    mx="auto"
                    mb={1}
                  >
                    {currentStep > index ? <FiCheck /> : index }
                  </Flex>
                  <Text fontSize="sm" display={{ base: 'none', md: 'block' }}>{title}</Text>
                </Box>
              ))}
            </Flex>
            </Box>
          )}

          {/* Form content */}
          {!hideFormDueToDraft && (
            <Box
              bg="white"
              p={{ base: 4, md: 8 }}
              borderRadius="lg"
              boxShadow="md"
            >
              {renderStepForm()}

              {/* Navigation buttons */}
              {currentStep > 0 && (
                <Flex mt={8} justify="space-between" direction={{ base: 'column-reverse', md: 'row' }}>
                  <Stack direction="row" spacing={4} mt={{ base: 4, md: 0 }}>
                    {currentStep > 1 && (
                      <Button
                        leftIcon={<FiChevronRight />}
                        variant="outline"
                        onClick={handlePrevious}
                      >
                        السابق
                      </Button>
                    )}
                    
                    {currentStep < totalSteps - 1 && (
                      <Button
                        rightIcon={<FiChevronLeft />}
                        colorScheme="primary"
                        onClick={handleNext}
                      >
                        التالي
                      </Button>
                    )}
                    
                  </Stack>
                  
                  <Button
                      leftIcon={<FiSave />}
                      variant="outline"
                      colorScheme="gray"
                      onClick={handleSaveProgress}
                      isLoading={isSaving}
                      loadingText="جاري الحفظ..."
                    >
                      حفظ كمسودة
                  </Button>
                </Flex>
              )}
            </Box>
          )}
        </Stack>
        )}

        {/* Success Modal */}
        <Modal isOpen={isOpen} onClose={handleSuccessModalClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader textAlign="center" color="green.500">تم تقديم الطلب بنجاح</ModalHeader>
            <ModalCloseButton />
            <ModalBody textAlign="center">
              <Box
                w="80px"
                h="80px"
                borderRadius="full"
                bg="green.100"
                color="green.500"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="3xl"
                mx="auto"
                mb={4}
              >
                <FiCheck />
              </Box>
              <Text fontSize="lg" mb={4}>
                تم تقديم طلبك بنجاح. سيتم مراجعته من قبل اللجنة المختصة.
              </Text>
              <Text fontSize="md" color="gray.600" fontWeight="bold">
                رقم الطلب: {submittedData?.application_number || 'غير متوفر'}
              </Text>
            </ModalBody>
            <ModalFooter justifyContent="center">
              <Button colorScheme="primary" onClick={handleSuccessModalClose}>
                العودة للصفحة الرئيسية
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        
        {/* مؤشر تحميل المسودة */}
        {isLoadingDraft && (
          <DraftLoadingIndicator message="جاري استرجاع المسودة المحفوظة..." />
        )}
      </>
    </Container>
  );
};

const ApplicationForm = () => {
  return (
    <NominationFormProvider>
      <ApplicationFormContent />
    </NominationFormProvider>
  );
};

export default ApplicationForm;